import { useCallback, useEffect, useState } from 'react';
import useWebSocket from './useWebSocket';

/**
 * Participant-side simulation state, driven entirely by the server.
 *
 * The phone holds no opinion about phase or score — it renders `view`, which the server
 * built through utils/simulationSerializer.forParticipant() and which therefore contains
 * only this team's deck and nothing about consequences until the reveal.
 *
 * Recovery is handled by re-emitting `join-session`: the server looks up the stored
 * simulationTeam, rebuilds the socket rooms and replies with `sim:state`. That is why
 * nothing here tries to cache or reconstruct state locally — a reconnect is a fresh
 * authoritative snapshot, not a merge.
 */
const useSimulationRun = ({ sessionId, participantId, participantName }) => {
  const socket = useWebSocket(sessionId);
  const { isConnected, reconnecting, on, off, emit } = socket;

  const [state, setState] = useState({
    runId: null,
    phase: null,
    roundIndex: 1,
    myFunctionKey: null,
    isCaptain: false,
    view: null,
    status: { lockedCount: 0, teamCount: 0, lockedFunctionKeys: [] },
  });
  const [draftCardId, setDraftCardId] = useState(null);
  const [lockedCardId, setLockedCardId] = useState(null);
  const [error, setError] = useState(null);
  /**
   * The session has moved on to something else (a new activity, a workbook, feedback,
   * or the end of the session). Once the simulation is over this is the cue to hand
   * the phone back to the normal session screen — until then participants stay on the
   * "look at the main screen" page while the trainer runs the final debrief.
   */
  const [sessionMovedOn, setSessionMovedOn] = useState(false);

  /** Ask the server who we are and what is happening. Safe to call repeatedly. */
  const announce = useCallback(() => {
    if (!sessionId || !participantId) return;
    emit('join-session', sessionId, { participantId, name: participantName });
  }, [emit, sessionId, participantId, participantName]);

  useEffect(() => { if (isConnected) announce(); }, [isConnected, announce]);

  useEffect(() => {
    const onState = (payload) => {
      setState((s) => ({ ...s, ...payload }));
      // A reconnect mid-round must not look like "you never chose".
      const mine = payload.status?.lockedFunctionKeys?.includes(payload.myFunctionKey);
      if (!mine) setLockedCardId(null);
    };

    const onRoundOpened = (payload) => {
      setState((s) => ({ ...s, ...payload }));
      setDraftCardId(null);
      setLockedCardId(null);
    };

    const onPhase = (payload) => setState((s) => ({ ...s, ...payload }));

    const onStatus = (status) =>
      setState((s) => (String(status.runId) === String(s.runId) ? { ...s, status } : s));

    // A teammate's draft — visible only inside our own team room.
    const onCardSelected = ({ cardId }) => setDraftCardId(cardId);

    const onLocked = ({ cardId }) => setLockedCardId(cardId);

    const onReveal = (payload) => setState((s) => ({ ...s, ...payload }));

    const onEnded = () => setState((s) => ({ ...s, phase: 'closed' }));

    const onError = ({ message }) => setError(message);

    // Team assignment arrives on the session room; re-announce so the server can put
    // us in the new team room and send back state.
    const onTeamsAssigned = ({ assignments }) => {
      const mine = (assignments || []).find((a) => String(a.participantId) === String(participantId));
      if (mine) announce();
    };

    const onMovedOn = () => setSessionMovedOn(true);
    // The same events the active-session screen listens for.
    const MOVED_ON = ['activity:launched', 'workbook:launched', 'overallFeedback:launched', 'session:ended'];
    MOVED_ON.forEach((evt) => on(evt, onMovedOn));

    on('sim:state', onState);
    on('sim:round-opened', onRoundOpened);
    on('sim:phase-changed', onPhase);
    on('sim:submission-status', onStatus);
    on('sim:card-selected', onCardSelected);
    on('sim:locked-in', onLocked);
    on('sim:reveal', onReveal);
    on('sim:ended', onEnded);
    on('sim:error', onError);
    on('sim:teams-assigned', onTeamsAssigned);

    return () => {
      MOVED_ON.forEach((evt) => off(evt, onMovedOn));
      off('sim:state', onState);
      off('sim:round-opened', onRoundOpened);
      off('sim:phase-changed', onPhase);
      off('sim:submission-status', onStatus);
      off('sim:card-selected', onCardSelected);
      off('sim:locked-in', onLocked);
      off('sim:reveal', onReveal);
      off('sim:ended', onEnded);
      off('sim:error', onError);
      off('sim:teams-assigned', onTeamsAssigned);
    };
  }, [on, off, participantId, announce]);

  /** Express a preference — teammates see it, nobody else does. */
  const selectCard = useCallback((cardId) => {
    setDraftCardId(cardId);
    if (state.runId) emit('sim:card-select', { runId: state.runId, cardId });
  }, [emit, state.runId]);

  /** Commit. Queued if offline; the server handler is idempotent. */
  const lockIn = useCallback((cardId) => {
    if (!state.runId || !cardId) return;
    setLockedCardId(cardId);
    emit('sim:lock-in', { runId: state.runId, cardId });
  }, [emit, state.runId]);

  return {
    ...state,
    draftCardId,
    lockedCardId,
    error,
    sessionMovedOn,
    isConnected,
    reconnecting,
    selectCard,
    lockIn,
    refresh: announce,
  };
};

export default useSimulationRun;
